package ai.jessie.web.members

import ai.jessie.web.onboarding.roleLabel
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.Grid2Style
import ai.jessie.web.theme.LegendStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

// The invitation as a fieldset shown as a card; its submit sits in the card footer.
@Composable
internal fun invitationForm(state: MemberManagementUiState, actions: MemberManagementActions) {
    Form(
        attrs = {
            addEventListener("submit") {
                it.preventDefault()
                actions.invite()
            }
        }
    ) {
        Div(attrs = CardStyle.toAttrs()) {
            Fieldset(attrs = FieldsetStyle.toAttrs { if (state.busy) attr("disabled", "") }) {
                Legend(attrs = LegendStyle.toAttrs()) { Text("Person einladen") }
                Div(attrs = CardBodyStyle.toAttrs()) {
                    Div(attrs = Grid2Style.toAttrs()) {
                        inviteEmail(state, actions)
                        inviteRole(state, actions)
                    }
                }
            }
            Div(attrs = CardFootStyle.toAttrs()) {
                themedButton(
                    "Einladung prüfen",
                    {},
                    ButtonVariant.PRIMARY,
                    disabled = state.busy,
                    submit = true,
                )
            }
        }
    }
}

@Composable
private fun inviteEmail(state: MemberManagementUiState, actions: MemberManagementActions) {
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(INVITE_EMAIL_ID) }) { Text("E-Mail-Adresse") }
        Input(
            InputType.Email,
            attrs =
                FieldInputStyle.toAttrs {
                    id(INVITE_EMAIL_ID)
                    attr("name", "email")
                    value(state.email)
                    attr("required", "")
                    attr("autocomplete", "off")
                    invalidIf(INVITE_EMAIL_ID in state.error?.fields.orEmpty())
                    onInput { actions.email(it.value) }
                },
        )
    }
}

@Composable
private fun inviteRole(state: MemberManagementUiState, actions: MemberManagementActions) {
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(INVITE_ROLE_ID) }) { Text("Rolle") }
        Select(
            attrs =
                FieldInputStyle.toAttrs {
                    id(INVITE_ROLE_ID)
                    attr("name", "role")
                    invalidIf(INVITE_ROLE_ID in state.error?.fields.orEmpty())
                    onChange { actions.role(it.value.orEmpty()) }
                }
        ) {
            MEMBER_ROLES.forEach { role ->
                Option(role, attrs = { if (role == state.role) selected() }) {
                    Text(roleLabel(role))
                }
            }
        }
    }
}

private fun AttrsScope<*>.invalidIf(invalid: Boolean) {
    if (invalid) {
        attr("aria-invalid", "true")
        attr("aria-describedby", "onboarding-error")
    }
}

// Steps 4–5: nothing is sent until the owner confirms; cancelling (A7) sends nothing.
@Composable
internal fun memberChangeConfirmation(
    change: MemberChange,
    busy: Boolean,
    actions: MemberManagementActions,
    focusReturn: FocusReturn,
) {
    Div(
        attrs =
            CardStyle.toAttrs {
                id("member-change-confirmation")
                attr("role", "alertdialog")
                attr("aria-labelledby", "member-change-message")
                attr("tabindex", "-1")
            }
    ) {
        Div(attrs = CardBodyStyle.toAttrs()) {
            P(attrs = { id("member-change-message") }) { Text(change.confirmationText()) }
        }
        Div(attrs = CardFootStyle.toAttrs()) {
            themedButton(
                change.confirmLabel(),
                actions.confirm,
                ButtonVariant.PRIMARY,
                disabled = busy,
            )
            themedButton("Abbrechen", actions.cancel, disabled = busy)
        }
    }
    LaunchedEffect(change) {
        focusReturn.pending = true
        if (!AppGlobals.isExporting)
            (document.getElementById("member-change-confirmation") as? HTMLElement)?.focus()
    }
}

// Returns keyboard focus to the listing after confirm or cancel, not on first page render.
internal class FocusReturn {
    var pending = false
}
